import React from 'react';
import {connect} from 'react-redux';
import {AppDispatch, RootState} from "../../../../store/store";
import {Box, Button, Dialog, DialogTitle, Grid, TextField} from "@mui/material";
import Text from '../../../app/text/text';
import dayjs from "dayjs";
import {localize} from "../../../../helpers/localization";
import {dayjsOrAny} from "../../../../helpers/dayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import Transaction from "../../../../models/transaction";
import {addTransaction} from "../../../../controllers/patients";
import {IAppNotification} from "../../../../models/appNotification";
import {setAppNotification} from "../../../../store/actions/app";

type IProps = {
    patientId: string,
    meetingId?: string,
    onTransactionSaved: (transaction: Transaction) => void,
}

type IState = {
    open: boolean,
    saving: boolean,
    transaction: Transaction,
    formValidation: any,
}

const mapStateToProps = (state: RootState) => {
    return {
        lang: state.settings?.lang,
        dateFormat: state.settings.dateFormat
    };
}

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setAppNotification: (appNotification: IAppNotification) => {
            dispatch(setAppNotification(appNotification));
        },
    };
};

type ReduxType = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & IProps;

class AddTransactionDialog extends React.Component<ReduxType, IState> {
    public readonly state: IState = {
        open: false,
        saving: false,
        transaction: new Transaction({date: new Date()}),
        formValidation: {}
    }

    openDialog() {
        this.setState((state: IState) => {
            return {
                ...state,
                open: true
            };
        });
    }

    closeDialog() {
        this.setState((state: IState) => {
            return {
                ...state,
                open: false,
                transaction: new Transaction({date: new Date()}),
            };
        });
    }

    onChange(property: string, value: any) {
        this.setState((state: IState) => {
            let tmp: any = Object.assign({}, state.transaction);
            tmp[property] = value;
            state.transaction = tmp;
            return state;
        }, () => {
            this.isFormValid();
        });
    }

    async saveTransaction() {
        const transaction = new Transaction({
            date: this.state.transaction.date,
            amount: this.state.transaction.amount,
            patientId: this.props.patientId,
            meetingId: this.props.meetingId,
        });
        const success = await addTransaction(transaction);

        if (success) {
            this.props.setAppNotification({severity: 'success', message: 'Transaction saved successfully'});
            this.props.onTransactionSaved(transaction);
            this.closeDialog();
        } else {
            this.props.setAppNotification({severity: 'error', message: 'Error while saving transaction'});
        }
    }

    isFormValid() {
        let formValidation: any = {};
        if (!this.state.transaction.date) formValidation.date = 'error';
        if (
            !this.state.transaction.amount ||
            isNaN(parseFloat(`${this.state.transaction.amount}`)) ||
            this.state.transaction.amount <= 0
        ) formValidation.amount = 'error';
        return Object.keys(formValidation).length === 0;
    }

    render() {
        return (
            <Dialog
                open={this.state.open}
                onClose={this.closeDialog.bind(this)}
            >
                <form onSubmit={(e: any) => {e.preventDefault(); this.saveTransaction();}}>
                    <DialogTitle>
                        <Text>Add transaction</Text>
                    </DialogTitle>
                    <Box sx={{p: 2}}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    sx={{width: '100%'}}
                                    label={localize('Date', this.props.lang)}
                                    value={dayjsOrAny(this.state.transaction.date)}
                                    format={this.props.dateFormat}
                                    onChange={((newValue: string | null | undefined) => {
                                        const newDate = dayjs(newValue).isValid() ? dayjs(newValue).toDate() : undefined;
                                        this.onChange('date', newDate);
                                    })}
                                    slotProps={{textField: { required: true, error: this.state.formValidation.date === 'error'}}}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    sx={{width: '100%'}}
                                    type={'number'}
                                    label={localize('Amount', this.props.lang)}
                                    value={this.state.transaction.amount}
                                    error={this.state.formValidation.amount === 'error'}
                                    onChange={((event: React.ChangeEvent<HTMLInputElement>) => {
                                        this.onChange('amount', event.currentTarget.value);
                                    })}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{p:2, width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                        <Button
                            variant={'contained'}
                            color={'error'}
                            onClick={this.closeDialog.bind(this)}
                        >
                            <Text>Cancel</Text>
                        </Button>
                        <Button
                            variant={'contained'}
                            color={'success'}
                            onClick={this.saveTransaction.bind(this)}
                            disabled={this.state.saving || !this.isFormValid()}
                        >
                            <Text>Save</Text>
                        </Button>
                    </Box>
                </form>
            </Dialog>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(AddTransactionDialog);
